import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignAPI, contentAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Megaphone, Plus, Calendar, Users, Award, X, FileText, Upload, Image, CheckCircle, Clock, XCircle, ExternalLink } from 'lucide-react';
import './Campaigns.css';

function Campaigns() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const [filter, setFilter] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignAPI.getAll().then(res => res.data.campaigns)
  });

  const createMutation = useMutation({
    mutationFn: (data) => campaignAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setShowForm(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => campaignAPI.update(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setShowForm(false);
      setEditingCampaign(null);
      // Refresh the detail modal with updated data if it's open
      if (res?.data?.campaign) {
        setSelectedCampaign(res.data.campaign);
      } else {
        setSelectedCampaign(null);
      }
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => campaignAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setSelectedCampaign(null);
    }
  });

  const filtered = campaigns?.filter(c => filter === 'all' ? true : c.status === filter) || [];

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  const openCreate = () => {
    setEditingCampaign(null);
    setShowForm(true);
  };

  const openEdit = (campaign) => {
    setEditingCampaign(campaign);
    setShowForm(true);
    setSelectedCampaign(null);
  };

  return (
    <div className="campaigns-page">
      <div className="campaigns-header">
        <h1><Megaphone size={28} /> Campaigns</h1>
        {isAdmin && (
          <button className="btn-primary" onClick={openCreate}>
            <Plus size={18} /> New Campaign
          </button>
        )}
      </div>

      <div className="campaign-filters">
        {['all', 'draft', 'active', 'paused', 'completed'].map(s => (
          <button
            key={s}
            className={`filter-btn ${filter === s ? 'active' : ''}`}
            onClick={() => setFilter(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="campaigns-loading">Loading campaigns...</div>
      ) : filtered.length === 0 ? (
        <div className="campaigns-empty">
          <div className="empty-icon"><Megaphone size={48} color="#d1d5db" /></div>
          <h3>No campaigns {filter !== 'all' ? `with status "${filter}"` : 'yet'}</h3>
          <p>{isAdmin ? 'Create your first campaign to get started.' : 'Check back soon for new campaigns.'}</p>
        </div>
      ) : (
        <div className="campaigns-grid">
          {filtered.map(campaign => (
            <div key={campaign.id} className="campaign-card" onClick={() => setSelectedCampaign(campaign)}>
              <div className="campaign-card-header">
                <h3>{campaign.title}</h3>
                <span className={`campaign-status ${campaign.status}`}>{campaign.status}</span>
              </div>
              {campaign.description && (
                <p className="campaign-description">{campaign.description}</p>
              )}
              <div className="campaign-meta">
                {campaign.start_date && (
                  <span className="campaign-meta-item">
                    <Calendar size={14} /> {formatDate(campaign.start_date)} – {formatDate(campaign.end_date)}
                  </span>
                )}
                {campaign.target_participants && (
                  <span className="campaign-meta-item">
                    <Users size={14} /> {campaign.target_participants} participants
                  </span>
                )}
                {campaign.point_value && (
                  <span className="campaign-points">
                    <Award size={14} /> {campaign.point_value} pts
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selectedCampaign && (
        <CampaignDetail
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          isAdmin={isAdmin}
          onEdit={openEdit}
          onDelete={(id) => { if (window.confirm('Delete this campaign?')) deleteMutation.mutate(id); }}
          onStatusChange={(id, status) => updateMutation.mutate({ id, data: { status } })}
          isUpdating={updateMutation.isPending}
        />
      )}

      {/* Create / Edit form modal */}
      {showForm && (
        <CampaignForm
          campaign={editingCampaign}
          onSubmit={(data) => {
            if (editingCampaign) {
              updateMutation.mutate({ id: editingCampaign.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
          onClose={() => { setShowForm(false); setEditingCampaign(null); }}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

/* ═══════════ CAMPAIGN DETAIL MODAL ═══════════ */
function CampaignDetail({ campaign, onClose, isAdmin, onEdit, onDelete, onStatusChange, isUpdating }) {
  const queryClient = useQueryClient();
  const [detailTab, setDetailTab] = useState('overview');
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  // Campaign content submissions
  const { data: contentData } = useQuery({
    queryKey: ['campaign-content', campaign.id],
    queryFn: () => campaignAPI.getContent(campaign.id).then(r => r.data),
  });

  // Campaign content library
  const { data: libraryData } = useQuery({
    queryKey: ['campaign-library', campaign.id],
    queryFn: () => campaignAPI.getLibrary(campaign.id).then(r => r.data.library),
  });

  // Submit content for this campaign
  const submitMutation = useMutation({
    mutationFn: (data) => contentAPI.submitContent({ ...data, campaign_id: campaign.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-content', campaign.id] });
      setShowSubmitForm(false);
    }
  });

  const submissions = contentData?.submissions || [];
  const stats = contentData?.stats || {};
  const library = libraryData || [];

  const statusIcon = (status) => {
    switch(status) {
      case 'approved': return <CheckCircle size={14} color="#10b981" />;
      case 'rejected': return <XCircle size={14} color="#ef4444" />;
      default: return <Clock size={14} color="#f59e0b" />;
    }
  };

  return (
    <div className="campaign-detail-overlay" onClick={onClose}>
      <div className="campaign-detail-modal campaign-detail-wide" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2>{campaign.title}</h2>
            <span className={`campaign-status ${campaign.status}`}>{campaign.status}</span>
          </div>
          <button className="btn-close" onClick={onClose} style={{ padding: '6px' }}>
            <X size={20} />
          </button>
        </div>

        {/* Detail tabs */}
        <div className="campaign-detail-tabs">
          <button className={`cd-tab ${detailTab === 'overview' ? 'active' : ''}`} onClick={() => setDetailTab('overview')}>
            Overview
          </button>
          <button className={`cd-tab ${detailTab === 'content' ? 'active' : ''}`} onClick={() => setDetailTab('content')}>
            <FileText size={14} /> Content
            {stats.total > 0 && <span className="cd-tab-badge">{stats.total}</span>}
          </button>
          {library.length > 0 && (
            <button className={`cd-tab ${detailTab === 'library' ? 'active' : ''}`} onClick={() => setDetailTab('library')}>
              <Image size={14} /> Assets
              <span className="cd-tab-badge">{library.length}</span>
            </button>
          )}
        </div>

        {/* OVERVIEW TAB */}
        {detailTab === 'overview' && (
          <>
            {campaign.description && (
              <div className="detail-section">
                <h4>Description</h4>
                <p>{campaign.description}</p>
              </div>
            )}

            <div className="detail-meta-grid">
              <div className="detail-meta-card">
                <label>Start Date</label>
                <div className="value">{formatDate(campaign.start_date)}</div>
              </div>
              <div className="detail-meta-card">
                <label>End Date</label>
                <div className="value">{formatDate(campaign.end_date)}</div>
              </div>
              <div className="detail-meta-card">
                <label>Target Participants</label>
                <div className="value">{campaign.target_participants || '—'}</div>
              </div>
              <div className="detail-meta-card">
                <label>Points</label>
                <div className="value">{campaign.point_value || '—'}</div>
              </div>
            </div>

            {/* Content stats summary */}
            {stats.total > 0 && (
              <div className="detail-section">
                <h4>Content Performance</h4>
                <div className="content-stats-row">
                  <div className="content-stat">
                    <span className="content-stat-val">{stats.total}</span>
                    <span className="content-stat-lbl">Submissions</span>
                  </div>
                  <div className="content-stat">
                    <span className="content-stat-val" style={{ color: '#10b981' }}>{stats.approved}</span>
                    <span className="content-stat-lbl">Approved</span>
                  </div>
                  <div className="content-stat">
                    <span className="content-stat-val" style={{ color: '#f59e0b' }}>{stats.pending}</span>
                    <span className="content-stat-lbl">Pending</span>
                  </div>
                  <div className="content-stat">
                    <span className="content-stat-val" style={{ color: '#ef4444' }}>{stats.rejected}</span>
                    <span className="content-stat-lbl">Rejected</span>
                  </div>
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="detail-section">
                <h4>Quick Actions</h4>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {campaign.status !== 'active' && (
                    <button className="btn-primary" style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer', fontSize: '13px' }}
                      disabled={isUpdating}
                      onClick={() => onStatusChange(campaign.id, 'active')}>
                      {isUpdating ? '...' : 'Activate'}
                    </button>
                  )}
                  {campaign.status === 'active' && (
                    <button style={{ padding: '8px 16px', background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer', fontSize: '13px' }}
                      disabled={isUpdating}
                      onClick={() => onStatusChange(campaign.id, 'paused')}>
                      Pause
                    </button>
                  )}
                  {campaign.status !== 'completed' && (
                    <button style={{ padding: '8px 16px', background: '#dbeafe', color: '#1e40af', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer', fontSize: '13px' }}
                      disabled={isUpdating}
                      onClick={() => onStatusChange(campaign.id, 'completed')}>
                      Mark Completed
                    </button>
                  )}
                  {campaign.status === 'completed' && (
                    <button style={{ padding: '8px 16px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer', fontSize: '13px' }}
                      disabled={isUpdating}
                      onClick={() => onStatusChange(campaign.id, 'draft')}>
                      Revert to Draft
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* CONTENT TAB */}
        {detailTab === 'content' && (
          <div className="campaign-content-section">
            <div className="campaign-content-header">
              <h4>Content Submissions ({submissions.length})</h4>
              {campaign.status === 'active' && (
                <button className="btn-primary" style={{ padding: '8px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => setShowSubmitForm(true)}>
                  <Upload size={14} /> Submit Content
                </button>
              )}
            </div>

            {showSubmitForm && (
              <ContentSubmitForm
                onSubmit={(data) => submitMutation.mutate(data)}
                onCancel={() => setShowSubmitForm(false)}
                isSubmitting={submitMutation.isPending}
              />
            )}

            {submissions.length === 0 ? (
              <div className="campaign-content-empty">
                <FileText size={36} color="#d1d5db" />
                <p>No content submitted for this campaign yet.</p>
                {campaign.status === 'active' && (
                  <button className="btn-primary" style={{ padding: '8px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer', fontSize: '13px' }}
                    onClick={() => setShowSubmitForm(true)}>
                    Be the first to submit!
                  </button>
                )}
              </div>
            ) : (
              <div className="campaign-submissions-list">
                {submissions.map(sub => (
                  <div key={sub.id} className="campaign-submission-card">
                    <div className="cs-header">
                      <div className="cs-author">
                        {sub.profile_photo_url ? (
                          <img src={sub.profile_photo_url} alt="" className="cs-avatar" />
                        ) : (
                          <div className="cs-avatar-placeholder">{(sub.first_name || '?')[0]}</div>
                        )}
                        <span className="cs-name">{sub.first_name} {sub.last_name}</span>
                      </div>
                      <div className="cs-status">
                        {statusIcon(sub.status)}
                        <span className={`cs-status-text ${sub.status}`}>{sub.status}</span>
                      </div>
                    </div>
                    <div className="cs-title">{sub.title}</div>
                    {sub.caption && <div className="cs-caption">{sub.caption}</div>}
                    {sub.media_url && (
                      <a href={sub.media_url} target="_blank" rel="noopener noreferrer" className="cs-media-link">
                        <ExternalLink size={12} /> {sub.media_type === 'link' ? 'View Post' : 'View Media'}
                      </a>
                    )}
                    <div className="cs-date">{new Date(sub.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* LIBRARY TAB */}
        {detailTab === 'library' && (
          <div className="campaign-content-section">
            <h4>Campaign Assets & Templates ({library.length})</h4>
            <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>Pre-approved content you can use for this campaign.</p>
            <div className="campaign-library-grid">
              {library.map(item => (
                <div key={item.id} className="library-card">
                  {item.thumbnail_url && <img src={item.thumbnail_url} alt="" className="library-thumb" />}
                  <div className="library-info">
                    <div className="library-title">{item.title}</div>
                    {item.description && <div className="library-desc">{item.description}</div>}
                    {item.caption && (
                      <div className="library-caption">
                        <strong>Suggested caption:</strong> {item.caption}
                      </div>
                    )}
                    {item.hashtags && (
                      <div className="library-hashtags">
                        {(typeof item.hashtags === 'string' ? JSON.parse(item.hashtags) : item.hashtags).map((h, i) => (
                          <span key={i} className="library-hashtag">#{h}</span>
                        ))}
                      </div>
                    )}
                    <span className="library-type">{item.media_type}</span>
                  </div>
                  {item.media_url && (
                    <a href={item.media_url} target="_blank" rel="noopener noreferrer" className="library-download">
                      <ExternalLink size={12} /> Open
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="detail-actions">
          {isAdmin && (
            <>
              <button className="btn-danger" onClick={() => onDelete(campaign.id)}>Delete</button>
              <button className="btn-primary" style={{ padding: '10px 20px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer' }}
                onClick={() => onEdit(campaign)}>Edit</button>
            </>
          )}
          <button className="btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════ CONTENT SUBMIT FORM ═══════════ */
function ContentSubmitForm({ onSubmit, onCancel, isSubmitting }) {
  const [form, setForm] = useState({
    title: '',
    caption: '',
    media_type: 'link',
    media_url: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="content-submit-form">
      <h5>Submit Content for this Campaign</h5>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title *</label>
          <input name="title" value={form.title} onChange={handleChange} required placeholder="e.g. Instagram Story - Product Feature" />
        </div>
        <div className="form-group">
          <label>Caption / Description</label>
          <textarea name="caption" value={form.caption} onChange={handleChange} placeholder="Describe your content..." rows={3} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Media Type</label>
            <select name="media_type" value={form.media_type} onChange={handleChange}>
              <option value="link">Link / URL</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="story">Story</option>
              <option value="reel">Reel</option>
            </select>
          </div>
          <div className="form-group">
            <label>Media URL *</label>
            <input name="media_url" value={form.media_url} onChange={handleChange} required placeholder="https://..." />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button type="button" onClick={onCancel} style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}>Cancel</button>
          <button type="submit" disabled={isSubmitting} style={{ padding: '8px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}>
            {isSubmitting ? 'Submitting...' : 'Submit for Review'}
          </button>
        </div>
      </form>
    </div>
  );
}

function CampaignForm({ campaign, onSubmit, onClose, isSubmitting }) {
  const [form, setForm] = useState({
    title: campaign?.title || '',
    description: campaign?.description || '',
    status: campaign?.status || 'draft',
    start_date: campaign?.start_date?.slice(0, 10) || '',
    end_date: campaign?.end_date?.slice(0, 10) || '',
    target_participants: campaign?.target_participants || '',
    point_value: campaign?.point_value || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      target_participants: form.target_participants ? parseInt(form.target_participants) : null,
      point_value: form.point_value ? parseInt(form.point_value) : null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    });
  };

  return (
    <div className="campaign-form-overlay" onClick={onClose}>
      <div className="campaign-form-modal" onClick={e => e.stopPropagation()}>
        <h2>{campaign ? 'Edit Campaign' : 'New Campaign'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input name="title" value={form.title} onChange={handleChange} required placeholder="Campaign title" />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} placeholder="What is this campaign about?" />
          </div>
          {campaign && (
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={form.status} onChange={handleChange}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" name="start_date" value={form.start_date} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" name="end_date" value={form.end_date} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Target Participants</label>
              <input type="number" name="target_participants" value={form.target_participants} onChange={handleChange} placeholder="e.g. 50" />
            </div>
            <div className="form-group">
              <label>Point Value</label>
              <input type="number" name="point_value" value={form.point_value} onChange={handleChange} placeholder="e.g. 100" />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-close" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}
              style={{ padding: '10px 20px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 500, cursor: 'pointer' }}>
              {isSubmitting ? 'Saving...' : campaign ? 'Update Campaign' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Campaigns;
